import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Phone, MessageCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { PHONE_NUMBER, PHONE_DISPLAY, PHONE_WHATSAPP } from "@/lib/constants";
import { trackCTAClick } from "@/lib/analytics";

interface InquirySuccessDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contactMethod?: "phone" | "email";
}

const InquirySuccessDialog = ({ open, onOpenChange, contactMethod = "phone" }: InquirySuccessDialogProps) => {
  const { t } = useTranslation();
  const WA_TEXT = t("inquiry.successDialog.whatsappMessage");
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="items-center text-center space-y-3">
          <div className="h-14 w-14 rounded-full bg-accent/15 flex items-center justify-center">
            <CheckCircle2 className="h-8 w-8 text-accent" strokeWidth={2} />
          </div>
          <DialogTitle className="text-xl">{t("inquiry.successDialog.title")}</DialogTitle>
          <DialogDescription className="text-sm leading-relaxed">
            {t("inquiry.successDialog.description", {
              contact: t(`inquiry.successDialog.contact.${contactMethod}`),
            })}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2 space-y-3">
          <p className="text-center text-sm font-medium text-foreground">
            {t("inquiry.successDialog.directCall")}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <Button
              asChild
              className="h-12 gradient-primary hover:opacity-90"
              onClick={() => trackCTAClick("phone", "inquiry_success_dialog", PHONE_DISPLAY)}
            >
              <a href={`tel:${PHONE_NUMBER}`} aria-label={t("inquiry.successDialog.callAriaLabel", { phone: PHONE_DISPLAY })}>
                <Phone className="h-4 w-4 mr-2" />
                {t("inquiry.successDialog.callButton")}
              </a>
            </Button>

            <Button
              asChild
              variant="outline"
              className="h-12 border-[#25D366] text-[#128C7E] hover:bg-[#25D366]/10 hover:text-[#128C7E]"
              onClick={() => trackCTAClick("whatsapp", "inquiry_success_dialog", PHONE_WHATSAPP)}
            >
              <a
                href={`https://wa.me/${PHONE_WHATSAPP}?text=${encodeURIComponent(WA_TEXT)}`}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={t("inquiry.successDialog.whatsappAriaLabel")}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {t("inquiry.successDialog.whatsappButton")}
              </a>
            </Button>
          </div>

          <Button
            type="button"
            variant="ghost"
            className="w-full h-10 text-sm text-muted-foreground"
            onClick={() => onOpenChange(false)}
          >
            {t("inquiry.successDialog.close")}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default InquirySuccessDialog;
